/**
 * drawingLayer — imperative canvas overlay synchronised to a
 * lightweight-charts v4 instance.
 *
 * Drawings are stored as {type, id, color, points} where every point is
 * a logical pair `{time, price}`. We redraw the canvas on pan / zoom / crosshair
 * move so shapes stay glued to their anchor points.
 *
 * Supported shapes:
 *   trendline  : 2 points, straight line
 *   hline      : 1 point, horizontal at that price
 *   vline      : 1 point, vertical at that time
 *   rect       : 2 points, axis-aligned rectangle
 *   fib        : 2 points, Fibonacci retracement (23.6/38.2/50/61.8/78.6)
 *   text       : 1 point + label string
 *
 * Public API:
 *   attachDrawingLayer(opts) -> controller
 *     opts = { container, chart, mainSeries, drawings, onChange }
 *   controller.setTool(toolName)     // 'trendline' | 'hline' | …  | null (idle)
 *   controller.setDrawings(list)
 *   controller.getDrawings()
 *   controller.destroy()
 */

const FIB_LEVELS = [0, 0.236, 0.382, 0.5, 0.618, 0.786, 1];

// Default colour palette per shape type.
const DEFAULT_COLOR = '#10b981';

let _seq = 0;
function uid() { _seq += 1; return `d${Date.now().toString(36)}${_seq}`; }

export function attachDrawingLayer({ container, chart, mainSeries, drawings = [], onChange }) {
  if (!container || !chart || !mainSeries) return null;

  // Build overlay canvas
  const canvas = document.createElement('canvas');
  canvas.style.position = 'absolute';
  canvas.style.inset = '0';
  canvas.style.pointerEvents = 'none';
  canvas.style.zIndex = '3';
  container.style.position = container.style.position || 'relative';
  container.appendChild(canvas);

  let currentTool = null;
  let shapes = [...(drawings || [])];
  let drafting = null;        // in-progress shape
  let hoverPoint = null;      // latest crosshair in logical coords

  const ctx = canvas.getContext('2d');

  const resize = () => {
    const dpr = window.devicePixelRatio || 1;
    canvas.width = container.clientWidth * dpr;
    canvas.height = container.clientHeight * dpr;
    canvas.style.width = `${container.clientWidth}px`;
    canvas.style.height = `${container.clientHeight}px`;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    redraw();
  };

  const ro = new ResizeObserver(() => resize());
  ro.observe(container);

  // Coordinate helpers
  const pxFor = (pt) => {
    if (!pt) return null;
    const x = chart.timeScale().timeToCoordinate(pt.time);
    const y = mainSeries.priceToCoordinate(pt.price);
    if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
    return { x, y };
  };

  const ptFor = (x, y) => {
    const time = chart.timeScale().coordinateToTime(x);
    const price = mainSeries.coordinateToPrice(y);
    if (time === null || price === null) return null;
    return { time, price };
  };

  // Rendering ────────────────────────────────────
  const strokeLine = (a, b, color, dash = null) => {
    ctx.beginPath();
    ctx.strokeStyle = color;
    ctx.lineWidth = 1.25;
    if (dash) ctx.setLineDash(dash); else ctx.setLineDash([]);
    ctx.moveTo(a.x, a.y);
    ctx.lineTo(b.x, b.y);
    ctx.stroke();
    ctx.setLineDash([]);
  };

  const drawShape = (s, inProgress = false) => {
    const color = s.color || DEFAULT_COLOR;
    const pts = (s.points || []).map(pxFor).filter(Boolean);
    if (pts.length < 1) return;

    switch (s.type) {
      case 'trendline': {
        if (pts.length < 2) return;
        strokeLine(pts[0], pts[1], color);
        dot(pts[0], color); dot(pts[1], color);
        break;
      }
      case 'hline': {
        const y = pts[0].y;
        ctx.beginPath();
        ctx.strokeStyle = color;
        ctx.lineWidth = 1;
        ctx.setLineDash([4, 3]);
        ctx.moveTo(0, y); ctx.lineTo(canvas.clientWidth, y);
        ctx.stroke();
        ctx.setLineDash([]);
        label({ x: 8, y }, `${formatPrice(s.points[0].price)}`, color);
        break;
      }
      case 'vline': {
        const x = pts[0].x;
        ctx.beginPath();
        ctx.strokeStyle = color;
        ctx.lineWidth = 1;
        ctx.setLineDash([4, 3]);
        ctx.moveTo(x, 0); ctx.lineTo(x, canvas.clientHeight);
        ctx.stroke();
        ctx.setLineDash([]);
        break;
      }
      case 'rect': {
        if (pts.length < 2) return;
        const [a, b] = pts;
        ctx.fillStyle = `${color}22`;
        ctx.strokeStyle = color;
        ctx.lineWidth = 1;
        const x = Math.min(a.x, b.x), y = Math.min(a.y, b.y);
        const w = Math.abs(b.x - a.x), h = Math.abs(b.y - a.y);
        ctx.fillRect(x, y, w, h);
        ctx.strokeRect(x, y, w, h);
        break;
      }
      case 'fib': {
        if (pts.length < 2) return;
        const [a, b] = pts;
        const pA = s.points[0].price, pB = s.points[1].price;
        const yFor = (lvl) => mainSeries.priceToCoordinate(pA + (pB - pA) * lvl);
        for (const lvl of FIB_LEVELS) {
          const y = yFor(lvl);
          if (!Number.isFinite(y)) continue;
          ctx.beginPath();
          ctx.strokeStyle = `${color}aa`;
          ctx.lineWidth = 1;
          ctx.setLineDash([2, 4]);
          ctx.moveTo(a.x, y); ctx.lineTo(b.x, y);
          ctx.stroke();
          ctx.setLineDash([]);
          label({ x: Math.min(a.x, b.x) + 6, y: y - 2 }, `${(lvl * 100).toFixed(1)}%`, color);
        }
        strokeLine(a, b, `${color}66`, [2, 4]);
        dot(a, color); dot(b, color);
        break;
      }
      case 'text': {
        label(pts[0], s.label || 'Note', color, true);
        break;
      }
      default:
        break;
    }

    if (inProgress) {
      ctx.strokeStyle = `${color}55`;
      ctx.strokeRect(0, 0, canvas.clientWidth - 1, canvas.clientHeight - 1);
    }
  };

  const dot = (p, color) => {
    ctx.beginPath();
    ctx.fillStyle = color;
    ctx.arc(p.x, p.y, 3, 0, Math.PI * 2);
    ctx.fill();
  };

  const label = (p, text, color, boxed = false) => {
    ctx.save();
    ctx.font = '11px Inter, system-ui, sans-serif';
    ctx.fillStyle = color;
    ctx.textBaseline = 'middle';
    if (boxed) {
      const metrics = ctx.measureText(text);
      const w = metrics.width + 8;
      const h = 18;
      ctx.fillStyle = 'rgba(17, 24, 39, 0.85)';
      ctx.fillRect(p.x, p.y - h / 2, w, h);
      ctx.strokeStyle = color;
      ctx.strokeRect(p.x, p.y - h / 2, w, h);
      ctx.fillStyle = color;
      ctx.fillText(text, p.x + 4, p.y);
    } else {
      ctx.fillText(text, p.x + 4, p.y - 6);
    }
    ctx.restore();
  };

  const formatPrice = (v) => {
    if (v == null || !Number.isFinite(v)) return '--';
    return v.toFixed(2);
  };

  const redraw = () => {
    if (!ctx || !canvas.width) return;
    ctx.clearRect(0, 0, canvas.clientWidth, canvas.clientHeight);
    for (const s of shapes) drawShape(s);
    if (drafting) drawShape(drafting, true);
  };

  // Keep pace with the chart
  const unsubTime = chart.timeScale().subscribeVisibleTimeRangeChange(redraw);
  const crosshairHandler = (param) => {
    if (param && param.time != null && mainSeries && param.point) {
      const price = mainSeries.coordinateToPrice(param.point.y);
      hoverPoint = price == null ? null : { time: param.time, price };
    } else {
      hoverPoint = null;
    }
    if (drafting) redraw();
  };
  chart.subscribeCrosshairMove(crosshairHandler);

  // Input
  const onDown = (ev) => {
    if (!currentTool) return;
    const rect = canvas.getBoundingClientRect();
    const x = ev.clientX - rect.left;
    const y = ev.clientY - rect.top;
    const pt = ptFor(x, y);
    if (!pt) return;

    if (currentTool === 'text') {
      const labelText = window.prompt('Note text:');
      if (labelText) {
        shapes = [...shapes, { type: 'text', id: uid(), color: DEFAULT_COLOR, points: [pt], label: labelText }];
        emit();
      }
      return;
    }
    if (currentTool === 'hline') {
      shapes = [...shapes, { type: 'hline', id: uid(), color: DEFAULT_COLOR, points: [pt] }];
      emit(); return;
    }
    if (currentTool === 'vline') {
      shapes = [...shapes, { type: 'vline', id: uid(), color: DEFAULT_COLOR, points: [pt] }];
      emit(); return;
    }
    // Multi-point tools (trendline / rect / fib) use a click-click flow.
    if (!drafting) {
      drafting = { type: currentTool, id: uid(), color: DEFAULT_COLOR, points: [pt] };
    } else {
      drafting.points.push(pt);
      if (drafting.points.length >= 2) {
        shapes = [...shapes, drafting];
        drafting = null;
        emit();
      }
    }
    redraw();
  };

  const onMove = (ev) => {
    if (!drafting) return;
    const rect = canvas.getBoundingClientRect();
    const x = ev.clientX - rect.left;
    const y = ev.clientY - rect.top;
    const pt = ptFor(x, y);
    if (!pt) return;
    const pts = drafting.points.slice(0, 1).concat([pt]);
    const preview = { ...drafting, points: pts };
    const backup = drafting;
    drafting = preview;
    redraw();
    drafting = backup;
  };

  const onKey = (ev) => {
    if (ev.key === 'Escape' && drafting) {
      drafting = null;
      redraw();
    }
  };

  const enableInput = (on) => {
    canvas.style.pointerEvents = on ? 'auto' : 'none';
    if (on) {
      canvas.addEventListener('mousedown', onDown);
      canvas.addEventListener('mousemove', onMove);
      window.addEventListener('keydown', onKey);
    } else {
      canvas.removeEventListener('mousedown', onDown);
      canvas.removeEventListener('mousemove', onMove);
      window.removeEventListener('keydown', onKey);
    }
  };

  const emit = () => {
    if (typeof onChange === 'function') onChange([...shapes]);
    redraw();
  };

  resize();
  redraw();

  return {
    setTool(name) {
      currentTool = name || null;
      drafting = null;
      enableInput(!!currentTool);
      redraw();
    },
    setDrawings(list) {
      shapes = [...(list || [])];
      redraw();
    },
    getDrawings() {
      return [...shapes];
    },
    clear() {
      shapes = [];
      emit();
    },
    undo() {
      shapes = shapes.slice(0, -1);
      emit();
    },
    destroy() {
      enableInput(false);
      try { ro.disconnect(); } catch { /* ignore */ }
      try { unsubTime && unsubTime(); } catch { /* ignore */ }
      try { chart.unsubscribeCrosshairMove(crosshairHandler); } catch { /* ignore */ }
      try { container.removeChild(canvas); } catch { /* ignore */ }
    },
  };
}


// ── Share-URL encoder / decoder ─────────────────────────────
// Drawings are small JSON — base64 them so users can paste a "share
// chart" URL that restores layout on load.

export function encodeLayout(layout) {
  try {
    const json = JSON.stringify(layout || {});
    // `btoa` doesn't handle unicode cleanly — but all keys in our
    // layouts are ASCII so it's fine.
    return btoa(unescape(encodeURIComponent(json)));
  } catch {
    return '';
  }
}

export function decodeLayout(s) {
  try {
    return JSON.parse(decodeURIComponent(escape(atob(s || ''))));
  } catch {
    return null;
  }
}
